import type { UserOverview } from '@racemap/sdk/schema/user';
import { matchPath, useLocation } from 'react-router';
import { useUserInfo } from 'src/lib/customHooks';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useIsAdmin } from 'src/lib/customHooks/useIsAdmin';
import { type UserAction, useUserActions } from '../useUserActions';

// `/admin/account/billing` is a redirect to the signed-in account, not an id.
const ObjectIdPattern = /^[0-9a-f]{24}$/i;

/**
 * The UserLink actions for the account an admin is looking at, so ⌘K reaches them without a menu
 * on the page. Null anywhere else, and on the admin's own account.
 */
export const useAccountActions = (): {
  user: UserOverview;
  actions: Array<UserAction>;
} | null => {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const currentUser = useCurrentUser();
  const userId = matchPath('/admin/account/:userId/*', location.pathname)?.params.userId;
  const inspectedId =
    isAdmin && userId != null && ObjectIdPattern.test(userId) && userId !== currentUser?.id
      ? userId
      : null;
  const { user } = useUserInfo(inspectedId);
  const sections = useUserActions(inspectedId ?? '', user);

  if (inspectedId == null || user == null) return null;
  // The account page is where this group shows up, so its own entry would go nowhere.
  const actions = sections.flat().filter((action) => action.to !== location.pathname);
  return { user, actions };
};
